import React, { useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SerializedEditorState } from "lexical";
import { RootState, AppDispatch } from "../../../store/store";
import {
  fetchCoachNoteTopicsAsync,
  uploadAttachmentAsync,
  saveCoachNoteAsync,
  updateCoachNoteAsync,
  fetchAllCoachNotesAsync
} from "../../../store/thunks/NotesThunk";
import {
  clearSaveNoteStatus,
  clearUploadStatus,
  removeUploadedAttachment,
  clearUploadedAttachments
} from "../../../store/slices/NotesSlice";
import { Topic } from "../../../services/NotesService";

// Import new components
import NoteEditor from './components/NoteEditor';
import ExternalLinks from './components/ExternalLinks';
import AttachmentManager from './components/AttachmentManager';
import SelectedTopicsDisplay from './components/SelectedTopicsDisplay';
import SessionDetails from './components/SessionDetails';
import NoteModalFooter from './components/NoteModalFooter';
import { useToast } from '../../../contexts/ToastContext';
import { CoachSessionType, defaultSessionType, isFutureDate, todayIsoDate, toIsoDate } from './sessionDetails';

interface AddNoteModalProps {
  userId: string,
  isOpen: boolean;
  onClose: () => void;
  onSave: (noteData: { externalLinks: string[], sessionOverview: string, selectedCategories: string[] }) => void;
  initialData: {
    externalLinks: string[];
    currentLink: string;
    sessionOverview: SerializedEditorState | null;
    selectedCategories: string[];
  };
  onDataChange: (data: {
    externalLinks: string[];
    currentLink: string;
    sessionOverview: SerializedEditorState | null;
    selectedCategories: string[];
  }) => void;
}

interface UploadedFile {
  file: File;
  uploadedId?: number;
  isUploading?: boolean;
  tempId?: string;
  name?: string;
  size?: number;
}

interface SelectedTopic {
  id: number;
  name: string;
}

type AutosaveStatus = 'idle' | 'autosaving' | 'saved' | 'Error';

interface AutosaveState {
  status: AutosaveStatus;
  lastSaved: Date | null;
  hasUnsavedChanges: boolean;
  isQueued: boolean;
}

const AddNoteModal: React.FC<AddNoteModalProps> = ({
  userId,
  isOpen,
  onClose,
  onSave,
  initialData,
  onDataChange
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    notes,
    topics,
    uploadedAttachments,
    topicsStatus,
    saveNoteStatus,
    uploadStatus,
    status: notesStatus,
    totalItems: notesTotalItems
  } = useSelector((state: RootState) => state.NotesStore);

  const { userAccount } = useSelector((state: RootState) => state.OrganizationListStore);
const { showErrorToast , showAlertToast} = useToast();
  // Use initialData for state initialization
  const [externalLinks, setExternalLinks] = useState<string[]>(initialData.externalLinks);
  const [currentLink, setCurrentLink] = useState(initialData.currentLink);
  const [sessionOverview, setSessionOverview] = useState<SerializedEditorState | null>(initialData.sessionOverview);
  // Local pending uploads so we can show file sizes immediately
  const [pendingUploads, setPendingUploads] = useState<UploadedFile[]>([]);

  // Date of Session and Coach Session Type. The type follows the member's
  // note history until the coach (or a restored draft) sets it.
  const [sessionDate, setSessionDate] = useState<string>(todayIsoDate());
  const [sessionType, setSessionType] = useState<CoachSessionType>('FIRST_SESSION');
  const [isSessionTypeSet, setIsSessionTypeSet] = useState(false);

  // Dropdown state
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState<SelectedTopic[]>([]);

  // Draft population state
  const [isDraftPopulated, setIsDraftPopulated] = useState(false);

  // Current note ID for update vs create
  const [currentNoteId, setCurrentNoteId] = useState<number | null>(null);

  // Autosave state
  const [autosaveState, setAutosaveState] = useState<AutosaveState>({
    status: 'idle',
    lastSaved: null,
    hasUnsavedChanges: false,
    isQueued: false
  });

  // Refs for autosave
  const autosaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSavedContentRef = useRef<string>('');
  const isSavingRef = useRef<boolean>(false);
  const modalContentRef = useRef<HTMLDivElement>(null);

  // Autosave utility functions
  const getCurrentContent = useCallback(() => {
    return JSON.stringify({
      externalLinks,
      sessionOverview: sessionOverview ? JSON.stringify(sessionOverview) : '',
      selectedTopics: selectedItems.map(item => item.id),
      sessionDate,
      sessionType
    });
  }, [externalLinks, sessionOverview, selectedItems, sessionDate, sessionType]);

  const hasContentChanged = useCallback(() => {
    const currentContent = getCurrentContent();
    return currentContent !== lastSavedContentRef.current;
  }, [getCurrentContent]);

  const performAutosave = useCallback(async (force: boolean = false) => {
    // Never run two saves at once: overlapping creates each POST a new draft.
    if (isSavingRef.current || (!force && !hasContentChanged())) {
      return;
    }

    // Get the current state values directly from state hooks to avoid stale closure
    const currentSelectedItems = selectedItems;
    const currentExternalLinks = externalLinks;
    const currentSessionOverview = sessionOverview;
    const currentUploadedAttachments = uploadedAttachments;

    isSavingRef.current = true;
    setAutosaveState(prev => ({ ...prev, status: 'autosaving' }));

    try {
      const attachmentIds = currentUploadedAttachments.map(attachment => attachment.id);
      const financialTopicIds = currentSelectedItems.map(item => item.id);

      const noteData = {
        financialTopicIds,
        externalLinks: currentExternalLinks,
        sessionOverview: currentSessionOverview ? JSON.stringify(currentSessionOverview) : '',
        attachmentIds,
        sessionDate,
        sessionType,
        isDraft: true // Autosave as draft
      };

      let result;
      if (currentNoteId) {
        // Update existing note
        result = await dispatch(updateCoachNoteAsync({ noteData, userId, noteId: currentNoteId }));
      } else {
        // Create new note
        result = await dispatch(saveCoachNoteAsync({ noteData, userId }));
      }

      if (saveCoachNoteAsync.fulfilled.match(result) || updateCoachNoteAsync.fulfilled.match(result)) {
        // If this was a create operation, set the note ID for future updates
        if (!currentNoteId && result.payload?.id) {
          setCurrentNoteId(result.payload.id);
        }

        lastSavedContentRef.current = getCurrentContent();
        setAutosaveState({
          status: 'saved',
          lastSaved: new Date(),
          hasUnsavedChanges: false,
          isQueued: false
        });

        // Clear "Saved" message after 2 seconds
        setTimeout(() => {
          setAutosaveState(prev => ({ ...prev, status: 'idle' }));
        }, 2000);
      } else {
        setAutosaveState(prev => ({
          ...prev,
          status: 'Error',
          hasUnsavedChanges: true,
          isQueued: false
        }));
      }
    } catch (error) {
      console.error('Autosave failed:', error);
      setAutosaveState(prev => ({
        ...prev,
        status: 'Error',
        hasUnsavedChanges: true,
        isQueued: false
      }));
    } finally {
      isSavingRef.current = false;
    }
  }, [dispatch, userId, getCurrentContent, hasContentChanged, uploadedAttachments, selectedItems, externalLinks, sessionOverview, sessionDate, sessionType, currentNoteId]);

  const scheduleAutosave = useCallback(() => {
    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current);
    }

    setAutosaveState(prev => ({ ...prev, hasUnsavedChanges: true }));


    autosaveTimeoutRef.current = setTimeout(() => {
      performAutosave();
    }, 750);
  }, [performAutosave]);

  const triggerImmediateAutosave = useCallback(() => {
    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current);
    }
    performAutosave(true);
  }, [performAutosave]);

  // Update local state when initialData changes (but not when populating from draft)
  React.useEffect(() => {
    if (!isDraftPopulated) {
      setExternalLinks(initialData.externalLinks);
      setCurrentLink(initialData.currentLink);
      setSessionOverview(initialData.sessionOverview);
    }

    // Don't set selectedItems from initialData here - wait for topics to load
    // We'll match by name once topics are available
  }, [initialData, isDraftPopulated]);

  // Match initial selected categories by name once topics are loaded
  React.useEffect(() => {
    if (topics.length > 0 && initialData.selectedCategories?.length > 0 && !isDraftPopulated) {
      const matchedTopics: SelectedTopic[] = [];

      // Find topics that match the initial selected category names
      topics.forEach(category => {
        category.topics.forEach(topic => {
          if (initialData.selectedCategories.includes(topic.name)) {
            matchedTopics.push(topic);
          }
        });
      });

      setSelectedItems(matchedTopics);
    }
  }, [topics, initialData.selectedCategories, isDraftPopulated]);

  // Load topics and fetch all notes when modal opens
  React.useEffect(() => {
    if (isOpen && topicsStatus === 'idle') {
      dispatch(fetchCoachNoteTopicsAsync());
    }

    // Fetch all notes to look for drafts when modal opens
    if (isOpen && userAccount?.id && !isDraftPopulated) {
      dispatch(fetchAllCoachNotesAsync({ userId }));
    }
  }, [isOpen, topicsStatus, dispatch, userId, userAccount?.id, isDraftPopulated]);

  // Populate draft when notes are loaded
  React.useEffect(() => {

    if (isOpen && notes.length > 0 && userAccount?.id && !isDraftPopulated) {
      // Filter notes by this coach and find the latest draft
      const coachNotes = notes.filter(note => (note.coach?.id ?? note.coachId) === userAccount.id);
      console.log('Filtered coach notes:', coachNotes);

      const latestDraft = coachNotes
        .filter(note => note.isDraft)
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())[0];



      if (latestDraft) {
        console.log('Populating from draft:', latestDraft);

        // Set the current note ID for updates
        setCurrentNoteId(latestDraft.id);

        // Populate external links
        if (latestDraft.externalLinks && latestDraft.externalLinks.length > 0) {
          setExternalLinks(latestDraft.externalLinks);
        }

        // Populate Date of Session and Coach Session Type
        const draftSessionDate = toIsoDate(latestDraft.sessionDate);
        if (draftSessionDate) {
          setSessionDate(draftSessionDate);
        }
        if (latestDraft.sessionType) {
          setSessionType(latestDraft.sessionType);
          setIsSessionTypeSet(true);
        }

        // Populate session overview
        let parsedSessionOverview = null;
        if (latestDraft.sessionOverview) {
          try {
            parsedSessionOverview = JSON.parse(latestDraft.sessionOverview);

            setSessionOverview(parsedSessionOverview);
          } catch (error) {
            console.error('Failed to load notes:', error);
          }
        }

        // Populate topics immediately if topics are already loaded
        if (topics.length > 0 && latestDraft.financialTopics) {
          const matchedTopics: SelectedTopic[] = [];

          latestDraft.financialTopics.forEach(draftTopic => {
            topics.forEach(category => {
              category.topics.forEach(topic => {
                if (topic.id === draftTopic.id) {
                  matchedTopics.push(topic);
                }
              });
            });
          });

          console.log('Setting selected topics from draft:', matchedTopics);
          setSelectedItems(matchedTopics);
        }

        // Note: selectedItems (topics) will be populated once topics are loaded
        // via the existing effect that matches initialData.selectedCategories

        // Update parent data to reflect populated draft
        const topicNames = latestDraft.financialTopics?.map(topic => topic.name) || [];
        onDataChange({
          externalLinks: latestDraft.externalLinks || [],
          currentLink: '',
          sessionOverview: parsedSessionOverview,
          selectedCategories: topicNames
        });
      }

      setIsDraftPopulated(true);
    }
  }, [isOpen, notes, userAccount?.id, isDraftPopulated, onDataChange]);

  // Populate selected topics from draft when both notes and topics are loaded
  React.useEffect(() => {
    if (isOpen && notes.length > 0 && topics.length > 0 && userAccount?.id && isDraftPopulated) {
      // Filter notes by this coach and find the latest draft
      const coachNotes = notes.filter(note => (note.coach?.id ?? note.coachId) === userAccount.id);
      const latestDraft = coachNotes
        .filter(note => note.isDraft)
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())[0];

      if (latestDraft && latestDraft.financialTopics) {
        const matchedTopics: SelectedTopic[] = [];

        // Match topics by ID from the draft
        latestDraft.financialTopics.forEach(draftTopic => {
          topics.forEach(category => {
            category.topics.forEach(topic => {
              if (topic.id === draftTopic.id) {
                matchedTopics.push(topic);
              }
            });
          });
        });

        setSelectedItems(matchedTopics);
      }
    }
  }, [isOpen, notes, topics, userAccount?.id, isDraftPopulated]);

  // Default Coach Session Type from the member's submitted notes (any coach)
  React.useEffect(() => {
    if (isOpen && notesStatus === 'succeeded' && !isSessionTypeSet) {
      setSessionType(defaultSessionType(notes, notesTotalItems));
    }
  }, [isOpen, notesStatus, notes, notesTotalItems, isSessionTypeSet]);

  // Clear upload status when modal opens
  React.useEffect(() => {
    if (isOpen) {
      dispatch(clearUploadStatus());
      dispatch(clearSaveNoteStatus());
    } else {
      // Trigger immediate autosave before closing if there are unsaved changes
      if (autosaveState.hasUnsavedChanges) {
        triggerImmediateAutosave();
      }
      // Reset draft populated state when modal closes
      setIsDraftPopulated(false);
      setCurrentNoteId(null);
    }
  }, [isOpen, dispatch, autosaveState.hasUnsavedChanges, triggerImmediateAutosave]);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;

      // Close dropdown if clicking outside the dropdown container
      if (isDropdownOpen && !target.closest('.topic-dropdown-container')) {
        setIsDropdownOpen(false);
      }

    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen, autosaveState.hasUnsavedChanges, scheduleAutosave]);

  // Autosave effects
  React.useEffect(() => {
    if (!isOpen) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (autosaveState.hasUnsavedChanges) {
        scheduleAutosave();
        e.preventDefault();
        e.returnValue = '';
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden && autosaveState.hasUnsavedChanges) {
        scheduleAutosave();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        triggerImmediateAutosave();
      }
    };

    const handleFocusOut = () => {
      if (autosaveState.hasUnsavedChanges) {
        scheduleAutosave();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('blur', handleFocusOut);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('blur', handleFocusOut);
    };
  }, [isOpen, autosaveState.hasUnsavedChanges, triggerImmediateAutosave]);

  // Initialize content reference
  React.useEffect(() => {
    if (isOpen) {
      lastSavedContentRef.current = getCurrentContent();
    }
  }, [isOpen, getCurrentContent]);

  // Trigger autosave when content changes (including external links)
  React.useEffect(() => {
    if (isOpen && isDraftPopulated && hasContentChanged()) {
      scheduleAutosave();
    }
  }, [isOpen, isDraftPopulated, externalLinks, sessionOverview, selectedItems, sessionDate, sessionType, scheduleAutosave, hasContentChanged]);

  // Debug sessionOverview changes
  React.useEffect(() => {
    console.log('sessionOverview state changed:', sessionOverview);
  }, [sessionOverview]);

  if (!isOpen) return null;

  // Close modal when clicking outside
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      triggerImmediateAutosave();
      onClose();
    }
  };

  const handleAddLink = () => {
    if (currentLink.trim()) {
      const newLinks = [...externalLinks, currentLink.trim()];
      setExternalLinks(newLinks);
      setCurrentLink('');

      // Sync with parent - convert SelectedTopic[] back to string[] for backward compatibility
      onDataChange({
        externalLinks: newLinks,
        currentLink: '',
        sessionOverview,
        selectedCategories: selectedItems.map(item => item.name)
      });

      // Trigger autosave
      scheduleAutosave();
    }
  };

  const handleRemoveLink = (index: number) => {
    const newLinks = externalLinks.filter((_, i) => i !== index);
    setExternalLinks(newLinks);

    // Sync with parent
    onDataChange({
      externalLinks: newLinks,
      currentLink,
      sessionOverview,
      selectedCategories: selectedItems.map(item => item.name)
    });

    // Trigger autosave
    scheduleAutosave();
  };

  const handleCurrentLinkChange = (value: string) => {
    setCurrentLink(value);

    // Sync with parent
    onDataChange({
      externalLinks,
      currentLink: value,
      sessionOverview,
      selectedCategories: selectedItems.map(item => item.name)
    });

    // Trigger autosave
    scheduleAutosave();
  };

  // Handle escape key to close modal
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddLink();
    }
  };

  const handleCheckboxChange = (topic: Topic) => {
    const isSelected = selectedItems.some(item => item.id === topic.id);
    const newSelectedItems = isSelected
      ? selectedItems.filter(item => item.id !== topic.id)
      : [...selectedItems, topic];

    setSelectedItems(newSelectedItems);

    // Sync with parent
    onDataChange({
      externalLinks,
      currentLink,
      sessionOverview,
      selectedCategories: newSelectedItems.map(item => item.name)
    });

    // Trigger autosave
    scheduleAutosave();
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const removeFile = (index: number) => {
    const attachmentToRemove = uploadedAttachments[index];
    if (attachmentToRemove) {
      dispatch(removeUploadedAttachment(attachmentToRemove.id));
    }
  };

  const removePendingFile = (tempId: string) => {
    setPendingUploads(prev => prev.filter(p => p.tempId !== tempId));
  };

  const openFileBrowser = () => {
    if (uploadedAttachments.length >= 10) {
      return; // Don't allow more than 10 files
    }

    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.multiple = true;
    fileInput.accept = '*/*';
    fileInput.onchange = async (event) => {
      const target = event.target as HTMLInputElement;
      const files = target.files;
      if (files) {
        const remainingSlots = 10 - uploadedAttachments.length - pendingUploads.length;
        const filesToAdd = Array.from(files).slice(0, remainingSlots);

        // Upload files to server using Redux thunk
        for (const file of filesToAdd) {
          // create a temp entry so we can show name and size immediately
          const tempId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
          const fileEntry: UploadedFile = {
            file,
            tempId,
            isUploading: true,
            name: file.name,
            size: file.size
          };
          setPendingUploads(prev => [...prev, fileEntry]);
          try {
            // start the upload; when it completes remove the pending entry (server-added attachment will appear via Redux)
            // dispatch returns a promise; handle completion to clean up pending UI
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            dispatch(uploadAttachmentAsync(file)).then(() => {
              setPendingUploads(prev => prev.filter(p => p.tempId !== tempId));
            }).catch((err) => {
              console.error('Upload failed for', file.name, err);
              // mark as not uploading so user can remove or retry
              setPendingUploads(prev => prev.map(p => p.tempId === tempId ? { ...p, isUploading: false } : p));
            });
          } catch (error) {
            console.error('Failed to upload file:', error);
          }
        }
      }
    };
    fileInput.click();
  };

  const handleSave = async () => {
    try {
      // Get current state values to avoid stale closure
      const currentSelectedItems = selectedItems;
      const currentExternalLinks = externalLinks;
      const currentSessionOverview = sessionOverview;
      const currentUploadedAttachments = uploadedAttachments;

      // Get uploaded attachment IDs
      const attachmentIds = currentUploadedAttachments.map(attachment => attachment.id);
       if (currentSelectedItems.length === 0) {
        showAlertToast(
          "Unable to Submit Coaching Note",
          "One or more topics must be selected. Please try again.",
        );
        return;
      }

      if (!sessionDate || isFutureDate(sessionDate)) {
        showAlertToast(
          "Unable to Submit Coaching Note",
          "Select a Date of Session that is today or earlier. Please try again.",
        );
        return;
      }

      // Get selected topic IDs
      const financialTopicIds = currentSelectedItems.map(item => item.id);

      const noteData = {
        financialTopicIds,
        externalLinks: currentExternalLinks,
        sessionOverview: currentSessionOverview ? JSON.stringify(currentSessionOverview) : '',
        attachmentIds,
        sessionDate,
        sessionType,
        isDraft: false // Manual save = final save, not draft
      };

      // Dispatch and wait for the result
      let result;
      if (currentNoteId) {
        // Update existing note
        result = await dispatch(updateCoachNoteAsync({ noteData, userId, noteId: currentNoteId }));
      } else {
        // Create new note
        result = await dispatch(saveCoachNoteAsync({ noteData, userId }));
      }

      // Check if the save was successful
      if (saveCoachNoteAsync.fulfilled.match(result) || updateCoachNoteAsync.fulfilled.match(result)) {
        // Success - call parent onSave for backward compatibility
        onSave({
          externalLinks: currentExternalLinks,
          sessionOverview: currentSessionOverview ? JSON.stringify(currentSessionOverview) : '',
          selectedCategories: currentSelectedItems.map(item => item.name)
        });

        // Reset form and close modal
        setExternalLinks([]);
        setCurrentLink('');
        setSessionOverview(null);
        setSelectedItems([]);
        setSessionDate(todayIsoDate());
        setIsSessionTypeSet(false);
        setPendingUploads([]); // Clear pending uploads too
        setCurrentNoteId(null); // Reset note ID
        // The note is submitted, so nothing is left to autosave on close
        if (autosaveTimeoutRef.current) {
          clearTimeout(autosaveTimeoutRef.current);
        }
        setAutosaveState({ status: 'idle', lastSaved: new Date(), hasUnsavedChanges: false, isQueued: false });
        dispatch(clearUploadedAttachments());
        onClose();

        console.log('Note saved successfully');
      } else if (saveCoachNoteAsync.rejected.match(result) || updateCoachNoteAsync.rejected.match(result)) {
        // Failure - handle error
        showAlertToast('Unable to Submit Coaching Note',"Your Coaching Note was unable to be submitted. Please try again.");
      }
    } catch (error) {
      showAlertToast('Unable to Submit Coaching Note',"Your Coaching Note was unable to be submitted. Please try again.");
    }
  };

  const handleEditorChange = (serializedState: SerializedEditorState) => {
    setSessionOverview(serializedState);

    // Sync with parent
    onDataChange({
      externalLinks,
      currentLink,
      sessionOverview: serializedState,
      selectedCategories: selectedItems.map(item => item.name)
    });

    // Trigger autosave
    scheduleAutosave();
  };

  return (
    <div
      className="
    fixed inset-0 flex items-center justify-center z-50 overflow-hidden 
    bg-[rgba(230,230,230,0.70)] backdrop-blur-[10px]
  "
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div
        ref={modalContentRef}
        className="bg-white rounded-xl w-full max-w-4xl h-[90vh] flex flex-col mx-4"
        onClick={e => e.stopPropagation()}
        role="document"
      >
        {/* Fixed Header */}


        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 pb-24">
          {/* Date of Session and Coach Session Type */}
          <SessionDetails
            sessionDate={sessionDate}
            sessionType={sessionType}
            onSessionDateChange={(value) => {
              setSessionDate(value);
              scheduleAutosave();
            }}
            onSessionTypeChange={(value) => {
              setSessionType(value);
              setIsSessionTypeSet(true);
              scheduleAutosave();
            }}
          />

          {/* External Links Section */}
          <ExternalLinks
            externalLinks={externalLinks}
            currentLink={currentLink}
            onCurrentLinkChange={handleCurrentLinkChange}
            onAddLink={handleAddLink}
            onRemoveLink={handleRemoveLink}
            onInputKeyDown={handleInputKeyDown}
            onInputBlur={() => {
              // Trigger autosave when user moves cursor away from input
              if (autosaveState.hasUnsavedChanges) {
                scheduleAutosave();
              }
            }}
          />

          {/* Session Overview Section */}
          <NoteEditor
            sessionOverview={sessionOverview}
            onEditorChange={handleEditorChange}
            isOpen={isOpen}
            isDraftPopulated={isDraftPopulated}
            currentNoteId={currentNoteId}
          />

          {/* Selected Topics and Uploaded Files Section - moved inside scrollable area so it scrolls */}
          {(uploadedAttachments.length > 0 || selectedItems.length > 0 || pendingUploads.length > 0) && (
            <div className="mt-6 py-4 bg-white rounded-md">
              {/* Uploaded Files */}
              {/* <AttachmentManager
                uploadedAttachments={uploadedAttachments}
                pendingUploads={pendingUploads}
                uploadStatus={uploadStatus}
                onOpenFileBrowser={openFileBrowser}
                onRemoveFile={removeFile}
                onRemovePendingFile={removePendingFile}
              />  */}

              {/* Selected Topics */}
              <SelectedTopicsDisplay
                selectedItems={selectedItems}
                onRemoveTopic={handleCheckboxChange}
              />
            </div>
          )}
        </div>

        {/* Fixed Footer */}
        <NoteModalFooter
          uploadedAttachments={uploadedAttachments}
          onOpenFileBrowser={openFileBrowser}
          topics={topics}
          topicsStatus={topicsStatus}
          selectedItems={selectedItems}
          isDropdownOpen={isDropdownOpen}
          onToggleDropdown={toggleDropdown}
          onCheckboxChange={handleCheckboxChange}
          autosaveStatus={autosaveState.status}
          onSave={handleSave}
          saveNoteStatus={saveNoteStatus}
          uploadStatus={uploadStatus}
        />
      </div>
    </div>
  );
};

export default AddNoteModal;
